/**
 * The tab at the screen's right edge that opens the consultant (Arjun, 2026-10-06). The consultant has no menu item: it
 * opens in the right-hand panel from the top bar's button, from Ctrl+J, and from this tab, which stays in reach on every
 * page while the panel is closed (lib/copilot/availability edgeTabShows). It asks as the top bar's button does, with
 * the page the person is on as the context and nothing drafted.
 *
 * From sm up only: on a phone the top bar's button is in reach and a fixed tab would sit over the page's content. Below
 * a side sheet (z-40) and the panel (z-30), so neither is covered; never printed.
 *
 * It stands just left of the page's scrollbar, never over it (#1233 review, MEDIUM): <main> scrolls the page and reaches
 * the window's right edge, and a classic scrollbar (15px on Windows) would otherwise sit under the tab, so its thumb
 * could not be grabbed at mid-height and a click on its track would open the consultant. The gutter is measured on
 * <main> whenever it resizes (a ResizeObserver reports once as it starts, too).
 */
import { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import { cn } from "@/lib/ui/cn";
import { scrollbarGutter } from "@/lib/copilot/panel-layout";

/** The page's scrollbar gutter: <main>'s, the shell's one scroll, as it resizes. 0 until measured. */
function useMainGutter(): number {
  const [gutter, setGutter] = useState(0);
  useEffect(() => {
    const main = document.querySelector("main");
    if (!main || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => setGutter(scrollbarGutter(main)));
    observer.observe(main);
    return () => observer.disconnect();
  }, []);
  return gutter;
}

export function ConsultantEdgeTab({ onOpen }: { onOpen: () => void }) {
  const gutter = useMainGutter();
  return (
    <button
      type="button"
      data-consultant-edge-tab=""
      data-no-print=""
      aria-label="Ask the consultant"
      onClick={onOpen}
      style={{ right: gutter }}
      className={cn(
        "fixed top-1/2 z-20 hidden -translate-y-1/2 sm:flex",
        "flex-col items-center gap-2 rounded-l-lg border border-r-0 border-border bg-surface-elevated px-1.5 py-3 shadow-md",
        "text-xs font-medium text-on-surface-muted transition-[padding,color] hover:pr-2.5 hover:text-on-surface",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
      )}
    >
      <Sparkles aria-hidden className="size-4 text-brand" />
      <span aria-hidden className="rotate-180 [writing-mode:vertical-rl]">
        Ask the consultant
      </span>
    </button>
  );
}
